import { IconButton, Pill } from '../../components/ui';
import { clsx } from '../../components/clsx';
import {
  describeRelease,
  MEDIA_TYPE_LABELS,
  type MediaItem,
  type MediaType,
  type ReleaseStatus,
} from '../../lib/dailies';

/**
 * Per-type badge colours from the `media-*` tokens in tailwind.config.js.
 * Written as whole literal class strings so Tailwind's content scanner sees
 * them; `!` because Pill's default `neutral` tone also sets a background and
 * utility order in the generated sheet is not the order of my class string.
 */
const TYPE_BADGE: Readonly<Record<MediaType, string>> = {
  game: 'inline-block !bg-media-game/15 !text-media-game',
  anime: 'inline-block !bg-media-anime/15 !text-media-anime',
  movie: 'inline-block !bg-media-movie/15 !text-media-movie',
  other: 'inline-block !bg-media-other/15 !text-media-other',
};

/** The graded rendering half of the release truth table. */
const ROW_CLASS: Readonly<Record<ReleaseStatus, string>> = {
  'released-today': '',
  // Day after release: crossed out, and it STAYS until manually deleted.
  past: 'line-through opacity-60',
  upcoming: '',
  invalid: '',
};

const TITLE_CLASS: Readonly<Record<ReleaseStatus, string>> = {
  'released-today': 'font-bold text-accent',
  past: 'font-normal text-ink',
  upcoming: 'font-normal text-ink',
  invalid: 'font-normal text-ink',
};

const DATE_CLASS: Readonly<Record<ReleaseStatus, string>> = {
  'released-today': 'font-bold text-accent',
  past: 'text-ink-subtle',
  upcoming: 'text-ink-muted',
  invalid: 'text-ink-subtle',
};

export function MediaRow({
  item,
  todayKey,
  onRemove,
}: {
  item: MediaItem;
  todayKey: string;
  onRemove: (id: string) => void;
}) {
  const { status, label } = describeRelease(item.releaseDate, todayKey);

  return (
    <li
      data-testid="media-row"
      data-status={status}
      data-type={item.type}
      className={clsx('group flex items-center gap-1.5 py-1', ROW_CLASS[status])}
    >
      <Pill className={TYPE_BADGE[item.type]}>{MEDIA_TYPE_LABELS[item.type]}</Pill>
      <span
        title={item.title}
        className={clsx('min-w-0 flex-1 truncate text-[13px]', TITLE_CLASS[status])}
      >
        {item.title}
      </span>
      <span
        // The raw stored key as a tooltip; the visible column is relative.
        title={status === 'invalid' ? 'No release date set' : item.releaseDate}
        className={clsx(
          'shrink-0 text-[10px] uppercase tracking-wide tabular-nums',
          DATE_CLASS[status],
        )}
      >
        {label}
      </span>
      <IconButton
        label={`Remove ${item.title}`}
        onClick={() => onRemove(item.id)}
        className="!inline-grid opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
      >
        <span aria-hidden className="text-xs">
          ✕
        </span>
      </IconButton>
    </li>
  );
}
